import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../redux/axios";

const initialState = {
  companyData: { data: [], currentPage: 0, totalPages: 0, totalItems: 0 },
  isFetchingCompanyData: false,
  isAddingCompanyData: false,
  isEditingCompanyData: false,
  isDeletingCompanyData: false,
  error: null,
  companyUserData: [],
  isFetchingCompanyUserList: false,
  isFetchingUserBalance: false,
  isFetchingCompanyTxns: false,
  multipleCompanyTemplatesData: [],
  isFetchingMultipleCompanyTemplates: false,
  singleCompanyTemplatesData: [],
  isFetchingSingleCompanyTemplates: false,
  isSavingUserTemplate: false,
  isDeletingUserTemplate: false,
  templatesRefreshTrigger: 0, 
};

export const getCompanyData = createAsyncThunk(
  "company/getData",
  async (params = {}, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      const {
        pageNumber = 0,
        pageSize = 10,
        sortBy = "companyName",
        sortDirection = "ASC",
        searchTerm,
        startDate,
        endDate,
        toGmt, 
      } = params;

      let url = `/companies/list?page=${pageNumber}&size=${pageSize}&sortBy=${sortBy}&sortDirection=${sortDirection.toUpperCase()}`;
      
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      if (startDate) {
       
        url += `&start=${encodeURIComponent(startDate)}`;
      }
      
      if (endDate) {
    
        url += `&end=${encodeURIComponent(endDate)}`;
      }
      
      if (toGmt) {
        url += `&toGmt=${encodeURIComponent(toGmt)}`;
      }

      const { data, status } = await axios.get(url, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return {
          data: data.data || [],
          currentPage: data.currentPage || 0,
          totalPages: data.totalPages || 0,
          totalItems: data.totalItems || 0,
        };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const addCompanyData = createAsyncThunk(
  "company/createData",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let url = `/companies`;
      const { data, status } = await axios.post(url, udata, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const editCompanyData = createAsyncThunk(
  "company/editData",
  async ({ id, udata }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let url = `/companies?companyId=${id}`;

      const { data, status } = await axios.put(url, udata, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const deleteCompanyData = createAsyncThunk(
  "company/deleteData",
  async ({ udata }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      let url = `/companies?companyId=${udata}`;

      const { data, status } = await axios.delete(url, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const companyUserList = createAsyncThunk(
  "company/user-list",
  async ({ udata }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let url = `/companies/by-company?companyId=${udata}`;
      const { data, status } = await axios.get(url, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const modifyCompanyBalance = createAsyncThunk(
  "company/modify-balance",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      const queryParam = new URLSearchParams();
      if (udata.companyId) queryParam.set("companyId", udata.companyId);
      if (udata.type) queryParam.set("type", udata.type);
      if (udata.amount) queryParam.set("amount", udata.amount);
      if (udata.walletFlag) queryParam.set("walletFlag", udata.walletFlag);
      let url = `/companies/balance?${queryParam.toString()}`;

      const { data, status } = await axios.put(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const updateFlag = createAsyncThunk(
  "company/flag",
  async (udata, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let url = `/companies/refresh-flag?param=company`;

      const { data, status } = await axios.post(
        url,
        {},
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const getCTxnHistory = createAsyncThunk(
  "company/transaction-history",
  async ({ companyId, startDate, endDate, statusType, types, toGmt }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;
      let url = "/companies/report";

      const params = new URLSearchParams();
      if (companyId) params.append("companyIds", companyId);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (statusType) params.append("status", statusType);
      if (types) params.append("type", types);
      if (toGmt !== undefined) params.append("toGmt", toGmt);

      const finalUrl = `${url}?${params.toString()}`;
      const { data, status } = await axios.get(finalUrl, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const getMultipleCompanyTemplates = createAsyncThunk(
  "company/multiple-templates",
  async ({ companyIds }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      let url = `/user-template/companies?companyIds=${companyIds}`;

      const { data, status } = await axios.get(url, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const getSingleCompanyTemplates = createAsyncThunk(
  "company/single-templates",
  async ({ companyId }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      let url = `/user-template/company/${companyId}`;

      const { data, status } = await axios.get(url, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const saveUserTemplate = createAsyncThunk(
  "company/save-user-template",
  async (templateData, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      let url = `/user-template/save`;

      const { data, status } = await axios.post(url, templateData, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const updateUserTemplate = createAsyncThunk(
  "company/update-user-template",
  async ({ templateId, templateData }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      let url = `/user-template/${templateId}`;

      const { data, status } = await axios.put(url, templateData, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const deleteUserTemplate = createAsyncThunk(
  "company/delete-user-template",
  async ({ templateId }, thunkAPI) => {
    try {
      const { auth } = thunkAPI.getState();
      let tokenStr = auth.user.accessToken;

      let url = `/user-template/${templateId}`;

      const { data, status } = await axios.delete(url, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });

      if (status.toString().startsWith("2")) {
        return { data, status };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (err) {
      console.error("err ===>", err);
      if (!err.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    clearStateCompany: (state) => {
      state.companyData = {
        data: [],
        currentPage: 0,
        totalPages: 0,
        totalItems: 0,
      };
      state.isFetchingCompanyData = false;
      state.isAddingCompanyData = false;
      state.isEditingCompanyData = false;
      state.isDeletingCompanyData = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    
    builder
      .addCase(getCompanyData.pending, (state) => {
        state.isFetchingCompanyData = true;
        state.error = null;
      })
      .addCase(getCompanyData.fulfilled, (state, action) => {
        state.isFetchingCompanyData = false;
        state.companyData = {
          data: action.payload.data || [],
          currentPage: action.payload.currentPage || 0,
          totalPages: action.payload.totalPages || 0,
          totalItems: action.payload.totalItems || 0,
        };
      })
      .addCase(getCompanyData.rejected, (state, action) => {
        state.isFetchingCompanyData = false;
        state.error = action.payload || "Failed to fetch company data.";
        
        if (
          action.payload?.status === 404 &&
          action.payload?.message === "No companies found."
        ) {
          state.companyData = {
            data: [],
            currentPage: 0,
            totalPages: 0,
            totalItems: 0,
          };
        }
      });

    
    builder
      .addCase(addCompanyData.pending, (state) => {
        state.isAddingCompanyData = true;
        state.error = null;
      })
      .addCase(addCompanyData.fulfilled, (state, action) => {
        state.isAddingCompanyData = false;
      })
      .addCase(addCompanyData.rejected, (state, action) => {
        state.isAddingCompanyData = false;
        state.error = action.payload || "Failed to add company data.";
      });

    
    builder
      .addCase(editCompanyData.pending, (state) => {
        state.isEditingCompanyData = true;
        state.error = null;
      })
      .addCase(editCompanyData.fulfilled, (state, action) => {
        state.isEditingCompanyData = false;
      })
      .addCase(editCompanyData.rejected, (state, action) => {
        state.isEditingCompanyData = false;
        state.error = action.payload || "Failed to edit company data.";
      });

   
    builder
      .addCase(deleteCompanyData.pending, (state) => {
        state.isDeletingCompanyData = true;
        state.error = null;
      })
      .addCase(deleteCompanyData.fulfilled, (state, action) => {
        state.isDeletingCompanyData = false;
      })
      .addCase(deleteCompanyData.rejected, (state, action) => {
        state.isDeletingCompanyData = false;
        state.error = action.payload || "Failed to delete company data.";
      });

   
    builder
      .addCase(companyUserList.pending, (state) => {
        state.isFetchingCompanyUserList = true;
        state.error = null;
      })
      .addCase(companyUserList.fulfilled, (state, action) => {
        state.isFetchingCompanyUserList = false;
        state.companyUserData = action.payload;
      })
      .addCase(companyUserList.rejected, (state, action) => {
        state.isFetchingCompanyUserList = false;
        state.error = action.payload || "Failed to fetch company user list.";
      });

    
    builder
      .addCase(modifyCompanyBalance.pending, (state) => {
        state.isFetchingUserBalance = true;
        state.error = null;
      })
      .addCase(modifyCompanyBalance.fulfilled, (state, action) => {
        state.isFetchingUserBalance = false;
        state.companyUserData = action.payload;
      })
      .addCase(modifyCompanyBalance.rejected, (state, action) => {
        state.isFetchingUserBalance = false;
        state.error = action.payload || "Failed to modify company balance.";
      });

    builder
      .addCase(getCTxnHistory.pending, (state) => {
        state.isFetchingCompanyTxns = true;
        state.error = null;
      })
      .addCase(getCTxnHistory.fulfilled, (state, action) => {
        state.isFetchingCompanyTxns = false;
      })
      .addCase(getCTxnHistory.rejected, (state, action) => {
        state.isFetchingCompanyTxns = false;
        state.error = action.payload || "Failed to get company transactions.";
      });

    
    builder
      .addCase(getMultipleCompanyTemplates.pending, (state) => {
        state.isFetchingMultipleCompanyTemplates = true;
        state.error = null;
      })
      .addCase(getMultipleCompanyTemplates.fulfilled, (state, action) => {
        state.isFetchingMultipleCompanyTemplates = false;
        const responseData = action.payload.data;
        
        if (Array.isArray(responseData)) {
          state.multipleCompanyTemplatesData = responseData;
        } else if (responseData && typeof responseData === "object") {
         
          state.multipleCompanyTemplatesData = [responseData];
        } else {
          state.multipleCompanyTemplatesData = [];
        }
      })
      .addCase(getMultipleCompanyTemplates.rejected, (state, action) => {
        state.isFetchingMultipleCompanyTemplates = false;
        state.error =
          action.payload || "Failed to fetch multiple company templates.";
        state.multipleCompanyTemplatesData = [];
      });

    
    builder
      .addCase(getSingleCompanyTemplates.pending, (state) => {
        state.isFetchingSingleCompanyTemplates = true;
        state.error = null;
      })
      .addCase(getSingleCompanyTemplates.fulfilled, (state, action) => {
        state.isFetchingSingleCompanyTemplates = false;
        const responseData = action.payload.data;
        
        if (Array.isArray(responseData)) {
          state.singleCompanyTemplatesData = responseData;
        } else if (responseData && typeof responseData === "object") {
         
          state.singleCompanyTemplatesData = [responseData];
        } else {
          state.singleCompanyTemplatesData = [];
        }
      })
      .addCase(getSingleCompanyTemplates.rejected, (state, action) => {
        state.isFetchingSingleCompanyTemplates = false;
        state.error =
          action.payload || "Failed to fetch single company templates.";
        state.singleCompanyTemplatesData = [];
      });

    
    builder
      .addCase(saveUserTemplate.pending, (state) => {
        state.isSavingUserTemplate = true;
        state.error = null;
      })
      .addCase(saveUserTemplate.fulfilled, (state, action) => {
        state.isSavingUserTemplate = false;
        state.templatesRefreshTrigger += 1; 
      })
      .addCase(saveUserTemplate.rejected, (state, action) => {
        state.isSavingUserTemplate = false;
        state.error = action.payload || "Failed to save user template.";
      });

    
    builder
      .addCase(updateUserTemplate.pending, (state) => {
        state.isSavingUserTemplate = true;
        state.error = null;
      })
      .addCase(updateUserTemplate.fulfilled, (state, action) => {
        state.isSavingUserTemplate = false;
        state.templatesRefreshTrigger += 1; 
      })
      .addCase(updateUserTemplate.rejected, (state, action) => {
        state.isSavingUserTemplate = false;
        state.error = action.payload || "Failed to update user template.";
      });

    
    builder
      .addCase(deleteUserTemplate.pending, (state) => {
        state.isDeletingUserTemplate = true;
        state.error = null;
      })
      .addCase(deleteUserTemplate.fulfilled, (state, action) => {
        state.isDeletingUserTemplate = false;
        state.templatesRefreshTrigger += 1; 
      })
      .addCase(deleteUserTemplate.rejected, (state, action) => {
        state.isDeletingUserTemplate = false;
        state.error = action.payload || "Failed to delete user template.";
      });
  },
});

export const { clearStateCompany } = companySlice.actions;
export const companySelector = (state) => state.company;
export default companySlice.reducer;
